import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { auth, functions } from '../api/firebase/firebase';
import { logError } from '../rollbar';
import { useOrganization } from './useOrganizations';

const selectUserState = createSelector([(state) => state.user], (user) => user ?? {});
const selectPermissionsState = createSelector([(state) => state.permissions], (permissions) => permissions ?? {});

export const useAuthUser = () => {
  const [token, setToken] = useState(null);

  useEffect(() => {
    const unsubscribe = auth().onAuthStateChanged((user) => {
      if (user) {
        user.getIdToken(true).then(setToken);
      } else {
        setToken(null);
      }
    });

    return () => unsubscribe(); // Clean up subscription
  }, []);
  return { currentUser: auth().currentUser, token };
};

/**
 * @returns {{ token: string | null, impersonatedToken: string | null }}
 */
export const useAuthToken = () => {
  const user = useSelector(selectUserState);
  const { token } = useAuthUser();
  const [impersonatedToken, setImpersonatedToken] = useState(null);
  const { isUserImpersonationEnabled } = useFlags();

  const { impersonatedOrganization, impersonatedDirectorId } = user ?? {};
  const isImpersonating = !!(isUserImpersonationEnabled && impersonatedOrganization && impersonatedDirectorId);

  // CCMS-8216: this MUST stay in an effect keyed on the director, and MUST NOT depend
  // on impersonatedToken. Firing it from the render body re-ran the callable on every
  // render for as long as the fetch kept failing, which exhausted the Firebase token
  // quota (auth/quota-exceeded) and locked impersonating admins out of CCMS entirely.
  useEffect(() => {
    if (!isImpersonating) {
      setImpersonatedToken(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await functions().httpsCallable('callables-users-generateIdTokenFromUserId')({
          userId: impersonatedDirectorId,
        });
        if (cancelled) return;

        const idToken = result?.data?.idToken;
        if (idToken) {
          setImpersonatedToken(idToken);
        } else {
          // The callable could not mint an ID token for the director. Fall back to the
          // admin's own token rather than signing the browser in as somebody else --
          // the old custom-token path swapped the authenticated user and could not
          // swap back, which is what made the failure self-sustaining.
          //
          // A real Error, not null: this is the exact condition that preceded the outage,
          // so the Rollbar item needs a stack and a sourcemapped frame to be diagnosable.
          logError(
            'Impersonation token unavailable for director',
            new Error('generateIdTokenFromUserId returned no idToken'),
            {
              impersonatedDirectorId,
            }
          );
        }
      } catch (error) {
        // Carry the callable's error code. Without it, quota exhaustion, an expired admin
        // session and an unexpected crash all land in Rollbar as one undifferentiated
        // message, and the next incident starts by correlating timestamps with GCP logs.
        if (!cancelled)
          logError('Error getting user impersonation token', error, { code: error?.code, impersonatedDirectorId });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isImpersonating, impersonatedDirectorId]);

  // Derived synchronously, not read straight from state. The effect above only clears
  // state after commit, so the render where impersonation ends would otherwise still
  // hand the previous director's token to ApolloClientWrapper -- one committed render
  // in which food-program requests go out under a credential we no longer hold.
  return { token, impersonatedToken: isImpersonating ? impersonatedToken : null };
};

export const useUser = () => {
  const user = useSelector(selectUserState);
  const organization = useOrganization();

  const isParent = useMemo(() => {
    return !!user?.claims?.parent;
  }, [user?.claims?.parent]);

  const isDirector = useMemo(() => {
    return !!user?.uid && !!organization?.createdBy && user.uid === organization.createdBy;
  }, [user, organization]);

  const isLocationAdminHighestClaim = useMemo(() => {
    return !user?.claims?.moxitAdmin && !user?.claims?.organizationAdmin && !!user?.claims?.locationAdmin;
  }, [user?.claims?.moxitAdmin, user?.claims?.organizationAdmin, user?.claims?.locationAdmin]);

  const isOrganizationAdmin = useMemo(() => {
    return !!user?.claims?.organizationAdmin;
  }, [user?.claims?.organizationAdmin]);

  const isLocationAdmin = useMemo(() => {
    return !!user?.claims?.locationAdmin;
  }, [user?.claims?.locationAdmin]);

  const isStaff = useMemo(() => {
    return !!(
      user?.claims?.moxitAdmin ||
      user?.claims?.organizationAdmin ||
      user?.claims?.locationAdmin ||
      user?.claims?.supportStaff ||
      user?.claims?.teacher
    );
  }, [user?.claims]);

  //This is both moxitAdmin and organizationAdmin. I didnt't want to change isOrganizationAdmin for fear of breaking something.
  const isStaffOrganizationAdmin = useMemo(() => {
    return !!(user?.claims?.moxitAdmin || user?.claims?.organizationAdmin);
  }, [user?.claims]);

  const isStaffNotOrganizationAdmin = useMemo(() => {
    return (
      !user?.claims?.moxitAdmin &&
      !user?.claims?.organizationAdmin &&
      !!(user?.claims?.locationAdmin || user?.claims?.supportStaff || user?.claims?.teacher)
    );
  }, [user?.claims]);

  const isDemoUser = useMemo(() => {
    return !!(user?.email === 'reseedit+amyprovider@gmail.com' || user?.email === 'sara@wonderschool.com');
  }, [user?.email]);

  return {
    ...user,
    isParent,
    isDirector,
    isLocationAdminHighestClaim,
    isOrganizationAdmin,
    isLocationAdmin,
    isStaff,
    isStaffOrganizationAdmin,
    isStaffNotOrganizationAdmin,
    isDemoUser,
  };
};

export const usePermissions = () => {
  return useSelector(selectPermissionsState);
};
